import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Trash2, BookOpen, GraduationCap, ArrowRight } from 'lucide-react';
import Tip from '../components/Tip';

import { motion } from 'framer-motion';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#1A4D2E', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B', '#EF4444', '#06B6D4'];

const SubjectsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', color: COLORS[0], description: '', lecturer_name: '', lecturer_consent: false });
  const [submitting, setSubmitting] = useState(false);

  // Define useCallback hooks BEFORE useEffect that uses them
  const fetchSubjects = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/subjects`);
      setSubjects(response.data);
    } catch (error) {
      console.error('Error fetching subjects:', error);
      toast.error('Failed to load subjects');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubjects();
  }, [fetchSubjects]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('storage_connected')) {
      toast.success(`${params.get('storage_connected')} connected successfully!`);
      window.history.replaceState({}, '', '/subjects');
    }
    if (params.get('storage_error')) {
      toast.error(`Failed to connect ${params.get('storage_error')}. Please try again.`);
      window.history.replaceState({}, '', '/subjects');
    }
  }, [location.search]);const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/subjects`, formData);
      toast.success('Subject created!');
      setOpen(false);
      setFormData({ name: '', color: COLORS[0], description: '', lecturer_name: '', lecturer_consent: false });
      fetchSubjects();
    } catch (error) {
      console.error('Error creating subject:', error);
      toast.error('Failed to create subject');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure? This will delete all lectures in this subject.')) return;

    try {
      await axios.delete(`${API}/subjects/${id}`);
      toast.success('Subject deleted');
      fetchSubjects();
    } catch (error) {
      console.error('Error deleting subject:', error);
      toast.error('Failed to delete subject');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96">Loading...</div>;
  }

  return (
    <div className="f500-page space-y-6 sm:space-y-8" data-testid="subjects-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4 border-b border-[var(--f500-line)] pb-6">
        <div>
          <p className="f500-eyebrow mb-2">— Subjects</p>
          <h1 className="f500-display text-3xl sm:text-4xl">Your courses, organized.</h1>
          <p className="f500-body text-base mt-2">Group lectures and track mastery per subject.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
            <button className="f500-btn f500-btn-primary w-full sm:w-auto" data-testid="add-subject-button">
              <Plus className="w-4 h-4" />
              Add Subject
            </button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md rounded-sm" data-testid="subject-dialog">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">Add New Subject</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="name">Subject Name *</Label>
                <Input
                  id="name"
                  placeholder="e.g., Calculus, Physics, History"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="rounded-sm"
                  data-testid="subject-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Choose Color</Label>
                <div className="flex flex-wrap gap-2">
                  {COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setFormData({ ...formData, color })}
                      className={`w-10 h-10 rounded-sm border-2 transition-all ${
                        formData.color === color ? 'border-foreground scale-110' : 'border-transparent'
                      }`}
                      style={{ backgroundColor: color }}
                      data-testid={`color-${color}`}
                    />
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Brief description of this subject"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="rounded-sm resize-none"
                  data-testid="subject-description-textarea"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="lecturer_name">Lecturer Name</Label>
                <Input
                  id="lecturer_name"
                  placeholder="e.g., Prof. Mensah"
                  value={formData.lecturer_name}
                  onChange={(e) => setFormData({ ...formData, lecturer_name: e.target.value })}
                  className="rounded-sm"
                  data-testid="subject-lecturer-input"
                />
              </div>

              <label className="flex items-start gap-3 p-3 rounded-lg border border-emerald-500/20 bg-emerald-500/5 cursor-pointer select-none" data-testid="lecturer-consent-label">
                <input
                  type="checkbox"
                  checked={formData.lecturer_consent}
                  onChange={(e) => setFormData({ ...formData, lecturer_consent: e.target.checked })}
                  className="mt-0.5 accent-emerald-500 w-4 h-4"
                  data-testid="lecturer-consent-checkbox"
                />
                <span className="text-xs leading-relaxed text-muted-foreground">
                  I have confirmed with the lecturer that recording is permitted for this course.
                  <span className="block text-[10px] opacity-60 mt-0.5">This creates a timestamped consent record.</span>
                </span>
              </label>

              <div className="flex gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setOpen(false)}
                  className="flex-1 rounded-sm"
                  data-testid="cancel-subject-button"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 rounded-sm"
                  data-testid="submit-subject-button"
                >
                  {submitting ? 'Creating...' : 'Add Subject'}
                </Button>
              </div>
            </form>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
        {subjects.length === 0 ? (
          <Card className="col-span-full p-12 rounded-sm border border-border text-center" data-testid="subjects-empty">
            <BookOpen className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-30" />
            <h3 className="text-xl font-medium mb-2">No subjects yet</h3>
            <p className="text-muted-foreground mb-2">Start with one — you can always add more later.</p>
            <p className="text-xs text-muted-foreground mb-6 max-w-md mx-auto">
              Try <em>"Calculus 2"</em>, <em>"Organic Chemistry"</em>, or <em>"History 101"</em>. Each subject groups your lectures, flashcards, and exams.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2">
              <Button onClick={() => setOpen(true)} className="rounded-sm" data-testid="empty-add-subject">
                <Plus className="w-4 h-4 mr-2" />
                Add my first subject
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/lectures')}
                className="rounded-sm text-sm"
                data-testid="empty-skip-to-record"
              >
                Or skip and record now →
              </Button>
            </div>
          </Card>
        ) : (
          subjects.map((subject, index) => (
            <motion.div
              key={subject.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <Card
                className="p-6 rounded-xl border-2 hover:shadow-lg transition-all duration-300 cursor-pointer group bg-[var(--isp-glass)] border-[var(--isp-glass-border)] hover:border-opacity-60"
                style={{ borderColor: subject.color }}
                onClick={() => navigate(`/subjects/${subject.id}`)}
                data-testid={`subject-card-${subject.id}`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-xl"
                    style={{ backgroundColor: subject.color }}
                  >
                    {subject.name.charAt(0).toUpperCase()}
                  </div>
                  <Tip text="Delete this subject and all its lectures">
                    <Button aria-label="Delete"
                      variant="ghost"
                      size="icon"
                      onClick={(e) => { e.stopPropagation(); handleDelete(subject.id); }}
                      className="rounded-lg text-destructive hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`delete-subject-${subject.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </Tip>
                </div>

                <h3 className="text-lg font-bold mb-1 truncate text-[var(--isp-heading)]">{subject.name}</h3>
                {subject.description && (
                  <p className="text-sm text-[var(--isp-muted)] line-clamp-2 mb-3">{subject.description}</p>
                )}
                {subject.lecturer_name && (
                  <p className="text-xs text-[var(--isp-body)] mb-3">Prof. {subject.lecturer_name}</p>
                )}

                <div className="flex items-center justify-between mt-auto pt-3 border-t border-[var(--isp-divider)]">
                  <div className="flex items-center gap-1.5 text-xs text-[var(--isp-body)]">
                    <GraduationCap className="w-3.5 h-3.5" />
                    <span>{subject.lecture_count || 0} lecture{(subject.lecture_count || 0) !== 1 ? 's' : ''}</span>
                  </div>
                  <span className="text-xs text-[#002FA7] font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    View <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </Card>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
};

export default SubjectsPage;